import { Activity, ListChecks, Star, TrendingUp, Wallet } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { responderConsoleMockupCards, type ResponderConsoleMockupCard } from "@/design-system/registry/homepage";

/**
 * S5 sub-component · Responder Console Mockup
 *
 * Static high-fidelity preview of "你的数字分身的管理界面" — 5 mini cards
 * showing month-to-date calls, settlement, rating, history, version.
 *
 * Reuses the brutalist console-shell visual language but renders only static
 * placeholder numbers (per spec §5 TODO note — no real data wiring).
 */

const TONE_BG: Record<ResponderConsoleMockupCard["tone"], string> = {
  green: "bg-[#A3E635]",
  blue: "bg-[#3B82F6]",
  purple: "bg-[#8B5CF6]",
  orange: "bg-[#F97316]",
  yellow: "bg-[#FACC15]",
};

const TONE_TEXT: Record<ResponderConsoleMockupCard["tone"], string> = {
  green: "text-black",
  blue: "text-white",
  purple: "text-white",
  orange: "text-white",
  yellow: "text-black",
};

const TONE_ICON: Record<ResponderConsoleMockupCard["tone"], LucideIcon> = {
  green: Activity,
  blue: Wallet,
  purple: Star,
  orange: ListChecks,
  yellow: TrendingUp,
};

export function ResponderConsoleMockup() {
  return (
    <div className="border-[6px] border-black bg-white shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] h-full flex flex-col">
      {/* Top bar — mirrors Console Showcase header strip */}
      <div className="bg-black text-white px-5 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="bg-[#A3E635] text-black border-2 border-[#A3E635] px-2 py-0.5 text-[10px] font-black tracking-widest uppercase">
            Mockup
          </span>
          <span className="text-[11px] font-black tracking-widest uppercase text-white/80">
            数字分身 · 管理界面
          </span>
        </div>
        <span className="text-[10px] font-black tracking-widest uppercase text-white/60">
          STATIC PREVIEW
        </span>
      </div>

      <div className="p-5 sm:p-6 flex-1 flex flex-col">
        <div className="grid gap-3 sm:grid-cols-2 flex-1">
          {responderConsoleMockupCards.map((card) => {
            const Icon = TONE_ICON[card.tone];
            return (
              <div
                key={card.label}
                className="border-2 border-black bg-white shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] flex"
              >
                <div
                  className={`w-12 flex-shrink-0 flex items-center justify-center border-r-2 border-black ${TONE_BG[card.tone]} ${TONE_TEXT[card.tone]}`}
                >
                  <Icon className="h-5 w-5" strokeWidth={2.5} />
                </div>
                <div className="p-3 flex-1 min-w-0">
                  <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                    {card.label}
                  </p>
                  <p className="mt-1 text-lg font-black text-black leading-tight truncate">
                    {card.value}
                  </p>
                  <p className="mt-1 text-[11px] font-bold text-zinc-600">
                    {card.delta}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 border-t-2 border-dashed border-zinc-300 pt-3">
          <p className="text-[11px] font-bold text-zinc-500 leading-relaxed">
            上面 5 张卡片皆为占位预览。Console 上线后会接入真实
            <span className="font-black text-zinc-700"> result_package </span>
            数据流。
          </p>
        </div>
      </div>
    </div>
  );
}
